import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { formatCZK } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { History, Download, Plus, X, AlertTriangle, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { day, todayIso, plusDays, pragueDay, CYCLE_MONTHS, CYCLE_PERIOD, pl } from "@/components/superadmin/firm";

// Historie plateb (209): faktury vystavené a zaplacené před napojením. Uloží se jen do časové
// osy se skutečnými daty — nic se neodesílá, nevystavuje ani nezamyká. „Zaplaceno do“ se
// změní, jen když ho superadmin výslovně potvrdí.

const addMonths = (iso, n) => {
  const [y, m, d] = iso.split("-").map(Number);
  const t = new Date(Date.UTC(y, m - 1 + n, 1));
  const last = new Date(Date.UTC(t.getUTCFullYear(), t.getUTCMonth() + 1, 0)).getUTCDate();
  t.setUTCDate(Math.min(d, last));
  return t.toISOString().slice(0, 10);
};
let seq = 0;
// 225: datum po dnešku (pražský den) — Fakturoid ho umí mít překlepem, server by odmítl celé uložení.
const isFuture = (iso) => !!iso && String(iso).slice(0, 10) > todayIso();
const manualRow = (amount) => ({ key: `m${++seq}`, source: "manual", checked: true, number: "", issued_on: "", paid_on: "", amount: String(amount || "") });

export function PaymentHistoryCard({ c, price, faConfigured, onChanged }) {
  const [mode, setMode] = useState(null);
  return (
    <section className="bg-card border border-border rounded-[18px] p-4 space-y-2.5" data-testid="section-history">
      <h3 className="text-sm font-bold flex items-center gap-1.5"><History size={16} className="text-muted-foreground" aria-hidden="true" /> Historie plateb</h3>
      <p className="text-xs text-muted-foreground">Faktury z doby před napojením. Uloží se do časové osy se skutečnými daty — nic se neodešle ani nevystaví.</p>
      <div className="flex flex-col sm:flex-row gap-2">
        <Button size="sm" variant="outline" disabled={!faConfigured} onClick={() => setMode("fa")} className="w-full sm:w-auto" data-testid="history-from-fakturoid"
          title={faConfigured ? undefined : "Nejdřív napoj Fakturoid (tlačítko v hlavičce)"}>
          <Download size={14} aria-hidden="true" /> Načíst z Fakturoidu
        </Button>
        <Button size="sm" variant="outline" onClick={() => setMode("manual")} className="w-full sm:w-auto" data-testid="history-manual">
          <Plus size={14} aria-hidden="true" /> Přidat ručně
        </Button>
      </div>
      {!faConfigured && <p className="text-[11px] text-muted-foreground">Načtení z Fakturoidu půjde po napojení (tlačítko Fakturoid v hlavičce).</p>}
      {mode && <HistoryDialog mode={mode} c={c} price={price} onClose={() => setMode(null)} onSaved={onChanged} />}
    </section>
  );
}

function HistoryDialog({ mode, c, price, onClose, onSaved }) {
  const months = CYCLE_MONTHS[c.billing?.cycle || "month"] || 1;
  const periodPrice = (price || 0) * months;
  const [rows, setRows] = useState(() => (mode === "manual" ? [manualRow(periodPrice)] : []));
  const [subject, setSubject] = useState(null);
  const [confirmed, setConfirmed] = useState(false);     // kontakt vybral superadmin ručně → uložit i místo dosavadního
  const [cands, setCands] = useState([]);
  const [loading, setLoading] = useState(mode === "fa");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [setPU, setSetPU] = useState(!c.billing?.paid_until);
  const [pu, setPu] = useState("");
  const [puTouched, setPuTouched] = useState(false);

  const load = async ({ subjectId, choose } = {}) => {
    setLoading(true); setError(null);
    try {
      const params = subjectId ? { subject_id: subjectId } : choose ? { choose: 1 } : {};
      const { data } = await api.get(`/admin/companies/${c.id}/billing/fakturoid-invoices`, { params });
      setSubject(data.subject); setCands(data.candidates || []); setConfirmed(!!subjectId);
      setRows((prev) => [
        ...(data.invoices || []).map((i) => ({ key: `f${i.id}`, source: "fa", fakturoid_invoice_id: i.id, number: i.number, issued_on: i.issued_on || "",
          paid_on: i.paid_on || "", amount: String(i.total ?? ""), url: i.url, status: i.status, line: i.line,
          imported: i.imported, system: i.system, future: isFuture(i.paid_on) || isFuture(i.issued_on),
          checked: !i.imported && !i.system && i.status === "paid" && !isFuture(i.paid_on) && !isFuture(i.issued_on) })),
        ...prev.filter((r) => r.source === "manual"),
      ]);
    } catch (e) { setError(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (mode === "fa") load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const chosen = rows.filter((r) => r.checked && !r.imported && !r.system && !r.future && (r.issued_on || r.paid_on));
  // Návrh „zaplaceno do“: poslední zaplacená faktura (vybraná nebo už v historii) pokrývá období od
  // svého vystavení → konec = vystavení + 1 období − 1 den. Nezávisí na tom, kolik faktur je vybráno.
  const paidKey = rows.filter((r) => r.paid_on && (r.imported || (r.checked && !r.system))).map((r) => `${r.issued_on}|${r.paid_on}|${r.number}`).join(",");
  const suggestion = useMemo(() => {
    const paid = paidKey ? paidKey.split(",").map((x) => { const [issued, paidOn, number] = x.split("|"); return { start: issued || paidOn, number }; }) : [];
    const last = paid.filter((x) => x.start && x.start >= "2000-01-01").sort((a, b) => a.start.localeCompare(b.start)).pop();
    if (!last) return null;
    return { start: pragueDay(last.start), number: last.number, until: plusDays(addMonths(pragueDay(last.start), months), -1) };
  }, [paidKey, months]);
  useEffect(() => { if (!puTouched) setPu(suggestion?.until || c.billing?.paid_until || ""); }, [suggestion?.until]); // eslint-disable-line react-hooks/exhaustive-deps

  const upd = (key, patch) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const invalid = chosen.some((r) => isFuture(r.paid_on) || isFuture(r.issued_on));
  const canSave = !busy && !invalid && (chosen.length > 0 || (setPU && pu));

  const save = async () => {
    setBusy(true);
    try {
      const body = { subject_id: subject?.id || null, subject_confirmed: confirmed, items: chosen.map((r) => ({ issued_on: r.issued_on || null, paid_on: r.paid_on || null, amount: r.amount === "" ? null : Number(r.amount),
        invoice_no: r.number || null, fakturoid_invoice_id: r.fakturoid_invoice_id || null, invoice_url: r.url || null })) };
      if (setPU && pu) body.paid_until = pu;
      const { data } = await api.post(`/admin/companies/${c.id}/billing/history`, body);
      toast.success(`Historie doplněna · ${data.added} ${pl(data.added, "záznam", "záznamy", "záznamů")}${data.skipped ? ` (${data.skipped} už tam byl${data.skipped === 1 ? "" : "y"})` : ""}`);
      await onSaved?.();
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90dvh] overflow-y-auto" onOpenAutoFocus={(e) => { e.preventDefault(); e.currentTarget?.focus?.(); }} data-testid="history-dialog">
        <DialogHeader>
          <DialogTitle>Historie plateb</DialogTitle>
          <DialogDescription>{c.name} — {mode === "fa" ? "faktury z Fakturoidu" : "doplň faktury ručně"}. Nic se neodešle ani nevystaví.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {mode === "fa" && loading && <div className="flex items-center gap-2 text-sm text-muted-foreground py-4"><Loader2 size={16} className="animate-spin motion-reduce:animate-none" /> Hledám faktury ve Fakturoidu…</div>}
          {mode === "fa" && error && <p className="text-sm text-rose-600 dark:text-rose-400" role="alert">{error}</p>}
          {mode === "fa" && !loading && !error && !subject && (
            <div className="rounded-[12px] bg-secondary px-3.5 py-3 text-sm space-y-2" data-testid="history-candidates">
              <p><b>Vyber kontakt firmy ve Fakturoidu.</b>{cands.length ? " Podle IČO ani názvu nebyl jednoznačný:" : " Podle IČO ani názvu jsem žádný nenašel — zkontroluj IČO firmy nebo přidej faktury ručně."}</p>
              {cands.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {cands.map((s) => (
                    <Button key={s.id} type="button" size="sm" variant="outline" onClick={() => load({ subjectId: s.id })} className="max-w-full" data-testid={`history-subject-${s.id}`}>
                      <span className="truncate">{s.name}{s.registration_no ? ` · IČO ${s.registration_no}` : ""}</span>
                    </Button>
                  ))}
                </div>
              )}
            </div>
          )}
          {mode === "fa" && subject && !loading && (
            <p className="text-xs text-muted-foreground">Kontakt: <b className="text-foreground">{subject.name}</b>{subject.registration_no ? ` · IČO ${subject.registration_no}` : ""} · {rows.filter((r) => r.source === "fa").length} faktur. Zaškrtni ty za předplatné Proyekty.{" "}
              <button type="button" onClick={() => { setRows((rs) => rs.filter((r) => r.source === "manual")); load({ choose: true }); }} className="font-semibold pp-ink hover:underline" data-testid="history-other-subject">Jiný kontakt?</button></p>
          )}

          {rows.length > 0 && (
            <div className="border border-border rounded-[14px] divide-y divide-border" data-testid="history-rows">
              {rows.map((r) => r.source === "fa" ? (
                <label key={r.key} className={`flex items-center gap-3 px-3 py-2.5 ${r.imported || r.system || r.future ? "opacity-60" : "cursor-pointer hover:bg-secondary/50"}`} data-testid={`history-row-${r.fakturoid_invoice_id}`}>
                  <input type="checkbox" className="w-5 h-5 accent-[hsl(var(--accent-orange))] shrink-0" checked={r.checked && !r.imported && !r.system && !r.future} disabled={r.imported || r.system || r.future}
                    onChange={(e) => upd(r.key, { checked: e.target.checked })} aria-label={`Faktura ${r.number}`} />
                  <span className="flex-1 min-w-0">
                    <span className="flex items-center gap-1.5 text-sm font-bold">{r.number}
                      {r.url && <a href={r.url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} aria-label="Otevřít ve Fakturoidu" className="text-muted-foreground hover:text-foreground"><ExternalLink size={13} /></a>}
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      vystavena {day(r.issued_on)} · {r.paid_on ? <>zaplacena <b className="text-success">{day(r.paid_on)}</b></> : <span className="text-amber-700 dark:text-amber-300 font-semibold">nezaplacená</span>}
                      {r.imported ? " · už v historii" : r.system ? " · vystavila aplikace, platbu spáruje sama" : ""}
                    </span>
                    {/* 225: datum v budoucnosti (překlep ve Fakturoidu) — řádek nejde vybrat, ostatní se uloží */}
                    {r.future && !r.imported && !r.system && <span className="block text-xs font-semibold text-rose-600 dark:text-rose-400" data-testid={`history-future-${r.fakturoid_invoice_id}`}>Datum je v budoucnosti — oprav ho ve Fakturoidu a načti znovu.</span>}
                    {r.line && <span className="block text-[11px] text-muted-foreground truncate">{r.line}</span>}
                  </span>
                  <span className="text-sm font-bold tabular-nums whitespace-nowrap">{formatCZK(Number(r.amount) || 0)}</span>
                </label>
              ) : (
                <div key={r.key} className="px-3 py-3 grid grid-cols-2 sm:grid-cols-[1fr_1fr_110px_1fr_auto] gap-2 items-end" data-testid="history-manual-row">
                  <div className="min-w-0"><label className="text-[11px] text-muted-foreground">Vystaveno</label>
                    <Input type="date" value={r.issued_on} max={todayIso()} onChange={(e) => upd(r.key, { issued_on: e.target.value })} className="h-10 w-full min-w-0" aria-label="Vystaveno" /></div>
                  <div className="min-w-0"><label className="text-[11px] text-muted-foreground">Zaplaceno</label>
                    <Input type="date" value={r.paid_on} max={todayIso()} onChange={(e) => upd(r.key, { paid_on: e.target.value })} className="h-10 w-full min-w-0" aria-label="Zaplaceno" /></div>
                  <div className="min-w-0"><label className="text-[11px] text-muted-foreground">Částka (Kč)</label>
                    <Input type="number" inputMode="decimal" value={r.amount} onChange={(e) => upd(r.key, { amount: e.target.value })} className="h-10 w-full min-w-0 tabular-nums" aria-label="Částka v Kč" /></div>
                  <div className="min-w-0"><label className="text-[11px] text-muted-foreground">Číslo faktury</label>
                    <Input value={r.number} onChange={(e) => upd(r.key, { number: e.target.value })} placeholder="2026-0011" className="h-10 w-full min-w-0" aria-label="Číslo faktury" /></div>
                  <button type="button" onClick={() => setRows((rs) => rs.filter((x) => x.key !== r.key))} aria-label="Odebrat řádek"
                    className="h-10 w-10 rounded-full grid place-items-center text-muted-foreground hover:bg-secondary justify-self-end col-span-2 sm:col-span-1"><X size={16} /></button>
                </div>
              ))}
            </div>
          )}
          {(mode === "manual" || !loading) && (
            <Button type="button" size="sm" variant="ghost" onClick={() => setRows((rs) => [...rs, manualRow(periodPrice)])} data-testid="history-add-row">
              <Plus size={14} /> {mode === "fa" ? "Přidat fakturu mimo Fakturoid" : "Další faktura"}
            </Button>
          )}
          {invalid && <p className="text-xs text-rose-600 dark:text-rose-400">Datum nemůže být v budoucnosti.</p>}

          <div className="rounded-[14px] bg-secondary/70 p-3.5 space-y-2" data-testid="history-paid-until">
            <label className="flex items-center justify-between gap-3 text-sm font-semibold">
              <span>Nastavit „Zaplaceno do“{c.billing?.paid_until ? <span className="font-normal text-muted-foreground"> (teď {day(c.billing.paid_until)})</span> : null}</span>
              <Switch checked={setPU} onCheckedChange={setSetPU} data-testid="history-set-pu" />
            </label>
            {setPU && (
              <>
                <Input type="date" value={pu} onChange={(e) => { setPu(e.target.value); setPuTouched(true); }} className="h-10 w-full sm:w-56 min-w-0 bg-card" aria-label="Zaplaceno do" data-testid="history-pu" />
                {suggestion && <p className="text-xs text-muted-foreground">Návrh: poslední zaplacená faktura{suggestion.number ? ` ${suggestion.number}` : ""} vystavená {day(suggestion.start)} + 1 {CYCLE_PERIOD[c.billing?.cycle || "month"]} → do <b>{day(suggestion.until)}</b>. Zkontroluj, že sedí.</p>}
                {pu && pu < todayIso() && (
                  <p className="text-xs text-amber-700 dark:text-amber-300 flex gap-1.5"><AlertTriangle size={14} className="shrink-0 mt-px" /> Datum je v minulosti — aplikace to vezme jako nezaplacené: vystaví fakturu na další období, majitel dostane upozornění a za 3 dny zámek.</p>
                )}
              </>
            )}
          </div>

          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pt-1">
            <Button variant="outline" onClick={onClose} className="w-full sm:w-auto">Zrušit</Button>
            <Button disabled={!canSave} onClick={save} className="w-full sm:w-auto font-bold" data-testid="history-save">
              {chosen.length ? `Uložit ${chosen.length} ${pl(chosen.length, "fakturu", "faktury", "faktur")} do historie` : "Uložit"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
